import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useProject } from '../context/ProjectContext';
import { getApiUrl, axiosConfig } from '../config/api';
import { supabase } from '../lib/supabase';
import './AccountSettings.css';

const AccountSettings = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { userPlan } = useProject();
  const [reportSettings, setReportSettings] = useState({
    logoUrl: '/logo.png',
    leftFooterText: 'Generated by Parrot SEO',
    rightFooterText: 'parrotseo.com',
    watermarkEnabled: true
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [licenseKey, setLicenseKey] = useState('');
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [licenseSaved, setLicenseSaved] = useState(false);
  
  // Normalize plan from Supabase users.plan (via ProjectContext -> userPlan)
  const normalizedPlan = (userPlan || 'basic').toLowerCase();

  // Map plan to friendly user type label for display
  const planLabelMap = {
    basic: 'Basic',
    individual: 'Individual',
    business: 'Business',
    enterprise: 'Enterprise',
    custom: 'Custom Plan',
    lifetime: 'Lifetime',
  };
  const userType = planLabelMap[normalizedPlan] || normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1);
  
  // Check if user has pro plan (individual, business, lifetime)
  const isProUser = ['individual', 'business', 'lifetime'].includes(normalizedPlan);

  // Ensure basic plan users always see default report settings
  useEffect(() => {
    if (normalizedPlan !== 'basic') return;

    const resetToDefault = async () => {
      const defaultSettings = {
        logoUrl: '/logo.png',
        leftFooterText: 'Generated by Parrot SEO',
        rightFooterText: 'parrotseo.com',
        watermarkEnabled: true,
      };

      setReportSettings(defaultSettings);
      setLogoPreview(null);
      localStorage.setItem('reportSettings', JSON.stringify(defaultSettings));

      // Best-effort: also reset in Supabase so future pro upgrades start from default
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (!sessionError && session?.user) {
          await supabase
            .from('report_settings')
            .upsert(
              {
                user_id: session.user.id,
                logo_url: null,
                left_footer_text: defaultSettings.leftFooterText,
                right_footer_text: defaultSettings.rightFooterText,
                watermark_enabled: defaultSettings.watermarkEnabled,
              },
              { onConflict: 'user_id' }
            );
        }
      } catch (error) {
        console.error('Failed to reset report settings for basic plan user:', error);
      }
    };

    resetToDefault();
  }, [normalizedPlan]);

  useEffect(() => {
    // 1) Load saved settings from localStorage as immediate fallback
    const savedSettings = localStorage.getItem('reportSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setReportSettings(parsed);
        if (parsed.logoUrl && parsed.logoUrl !== '/logo.png') {
          setLogoPreview(parsed.logoUrl);
        }
      } catch (error) {
        console.error('Error loading report settings from localStorage:', error);
      }
    }
    
    // 2) Load saved license from localStorage (future feature, UI-only for now)
    const savedLicense = localStorage.getItem('licenseKey');
    if (savedLicense) {
      setLicenseKey(savedLicense);
    }

    // 3) Load report settings directly from Supabase (no backend API calls)
    const loadFromSupabase = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session?.user) {
          return;
        }
        const userId = session.user.id;
        const { data, error } = await supabase
          .from('report_settings')
          .select('logo_url, left_footer_text, right_footer_text, watermark_enabled')
          .eq('user_id', userId)
          .single();

        if (!error && data) {
          const normalized = {
            logoUrl: data.logo_url || '/logo.png',
            leftFooterText: data.left_footer_text || 'Generated by Parrot SEO',
            rightFooterText: data.right_footer_text || 'parrotseo.com',
            watermarkEnabled: data.watermark_enabled !== undefined ? !!data.watermark_enabled : true,
          };
          setReportSettings(normalized);
          localStorage.setItem('reportSettings', JSON.stringify(normalized));
          if (normalized.logoUrl && normalized.logoUrl !== '/logo.png') {
            setLogoPreview(normalized.logoUrl);
          } else {
            setLogoPreview(null);
          }
        }
      } catch (error) {
        console.error('Failed to load report settings from Supabase:', error);
      }
    };

    loadFromSupabase();
  }, []);

  const processFile = async (file) => {
    if (!file) return;

    // Validate file type (PNG, JPG, JPEG only)
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const fileType = (file.type || '').toLowerCase();
    if (!allowedTypes.includes(fileType)) {
      alert('Please upload a PNG or JPG image file.');
      return;
    }

    // Validate file size (max 4MB - now we upload directly to Supabase Storage)
    const maxBytes = 4 * 1024 * 1024;
    if (file.size > maxBytes) {
      alert('Image size should be less than 4MB. Please compress your image and try again.');
      return;
    }

    try {
      // Get current user ID from Supabase session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        alert('Please sign in to upload a logo.');
        return;
      }

      const userId = session.user.id;
      const fileExt = file.name.split('.').pop();
      // Object path within the bucket: {user_id}/{timestamp}.{ext}
      const filePath = `${userId}/${Date.now()}.${fileExt}`;

      // Upload directly to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('report-logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false // Don't overwrite existing files
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        // If file already exists, try with a unique name
          if (uploadError.message?.includes('already exists')) {
            const uniqueFilePath = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          const { error: retryError } = await supabase.storage
            .from('report-logos')
            .upload(uniqueFilePath, file, {
              cacheControl: '3600',
              upsert: false
            });
          
          if (retryError) {
            alert('Failed to upload logo. Please try again.');
            return;
          }
          
          // Get public URL
          const { data: urlData } = supabase.storage
            .from('report-logos')
            .getPublicUrl(uniqueFilePath);
          
          setLogoPreview(urlData.publicUrl);
          setReportSettings(prev => ({
            ...prev,
            logoUrl: urlData.publicUrl
          }));
          return;
        }
        
        alert('Failed to upload logo. Please try again.');
        return;
      }

      // Get public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from('report-logos')
        .getPublicUrl(filePath);

      // Update preview and settings with the public URL
      setLogoPreview(urlData.publicUrl);
      setReportSettings(prev => ({
        ...prev,
        logoUrl: urlData.publicUrl
      }));
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Failed to upload logo. Please try again.');
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    await processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    await processFile(file);
  };

  const handleInputChange = (field, value) => {
    setReportSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleWatermark = () => {
    setReportSettings(prev => ({
      ...prev,
      watermarkEnabled: !prev.watermarkEnabled
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Prefer direct Supabase upsert so report_settings is always updated,
      // even if the backend API is misconfigured in some environments.
      let persistedToSupabase = false;
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (!sessionError && session?.user) {
          const userId = session.user.id;
          const { error: upsertError } = await supabase
            .from('report_settings')
            .upsert(
              {
                user_id: userId,
                logo_url: reportSettings.logoUrl || null,
                left_footer_text: reportSettings.leftFooterText || null,
                right_footer_text: reportSettings.rightFooterText || null,
                watermark_enabled: reportSettings.watermarkEnabled !== undefined
                  ? !!reportSettings.watermarkEnabled
                  : true,
              },
              { onConflict: 'user_id' }
            );

          if (upsertError) {
            console.error('Supabase report_settings upsert error:', upsertError);
          } else {
            persistedToSupabase = true;
          }
        }
      } catch (supabaseError) {
        console.error('Supabase session/report_settings error:', supabaseError);
      }

      // Fallback: if Supabase upsert failed (e.g., not using Supabase auth),
      // try the backend API using the existing token flow.
      if (!persistedToSupabase) {
        const token = localStorage.getItem('scanmaster_token');
        if (token) {
          await axios.post(
            getApiUrl('report-settings'),
            {
              logoUrl: reportSettings.logoUrl,
              leftFooterText: reportSettings.leftFooterText,
              rightFooterText: reportSettings.rightFooterText,
              watermarkEnabled: reportSettings.watermarkEnabled,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              },
              ...axiosConfig
            }
          );
        }
      }

      // Also persist locally for faster subsequent loads
      localStorage.setItem('reportSettings', JSON.stringify(reportSettings));

      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save report settings:', error);
      // Still update localStorage so the user does not lose changes
      localStorage.setItem('reportSettings', JSON.stringify(reportSettings));
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const handleReset = () => {
    const defaultSettings = {
      logoUrl: '/logo.png',
      leftFooterText: 'Generated by Parrot SEO',
      rightFooterText: 'parrotseo.com',
      watermarkEnabled: true
    };
    setReportSettings(defaultSettings);
    setLogoPreview(null);
    localStorage.setItem('reportSettings', JSON.stringify(defaultSettings));
  };

  const handleLicenseSave = async () => {
    setLicenseSaving(true);
    
    try {
      const token = localStorage.getItem('scanmaster_token');
      if (token) {
        // Save to Supabase via API
        await axios.post(
          getApiUrl('user/license'),
          { licenseKey },
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            ...axiosConfig
          }
        );
      }
      
      // Also save to localStorage as fallback
      localStorage.setItem('licenseKey', licenseKey);
      
      setLicenseSaving(false);
      setLicenseSaved(true);
      setTimeout(() => setLicenseSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save license:', error);
      // Still save to localStorage as fallback
      localStorage.setItem('licenseKey', licenseKey);
      setLicenseSaving(false);
      setLicenseSaved(true);
      setTimeout(() => setLicenseSaved(false), 3000);
    }
  };

  const header = (
    <header className="header">
      <div className="header-container">
        <div className="header-actions">
          <a href="/pricing" className="header-menu-item">
            Plans & Pricing
          </a>
          {user && (
            <div className="user-info">
              <button
                className="user-icon-button"
                onClick={() => navigate('/dashboard')}
                aria-label="Back to dashboard"
                title="Back to dashboard"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );

  return (
    <div className="seo-analyzer account-settings-page">
      {header}
      <div className="container">
        <div className="settings-container">
          <div className="settings-header">
            <div className="settings-header-left">
              <h1 className="settings-title">Account Settings</h1>
              <p className="settings-subtitle">Manage your account and preferences</p>
            </div>
            {activeTab === 'reports' && isProUser && (
              <div className="settings-header-actions">
                <button
                  type="button"
                  className="save-button"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="reset-button"
                  onClick={handleReset}
                >
                  Reset to Default
                </button>
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="settings-tabs">
            <button
              className={`settings-tab ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
            <button
              className={`settings-tab ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              Custom Reports
            </button>
            <button
              className={`settings-tab ${activeTab === 'license' ? 'active' : ''}`}
              onClick={() => setActiveTab('license')}
            >
              License
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'general' && (
            <div className="settings-section">
              <h2 className="section-title">Account Information</h2>
              <p className="section-description">
                View your account details and subscription information
              </p>

              <div className="settings-form">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <div className="form-value-display">
                    {user?.name || 'Not set'}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <div className="form-value-display">
                    {user?.email || 'Not set'}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">User Type</label>
                  <div className="user-type-badge-container">
                    <span className={`user-type-badge ${normalizedPlan}`}>
                      {userType}
                    </span>
                  </div>
                  <p className="form-hint">
                    {userType === 'Basic' 
                      ? 'You are on the Basic plan. Upgrade to Pro for advanced features.'
                      : 'You are on the Pro plan with access to all features.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="settings-section">
              {!isProUser ? (
                <div className="account-pro-lock">
                  <div className="account-pro-lock-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </div>
                  <div className="account-pro-lock-content">
                    <h2 className="account-pro-lock-title">Custom Reports is a Pro feature</h2>
                    <p className="account-pro-lock-text">
                      Upgrade your plan to unlock custom report branding, logos, and advanced customization options.
                    </p>
                    <button
                      type="button"
                      className="account-pro-lock-button"
                      onClick={() => window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer')}
                    >
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="section-title">Custom Reports</h2>
                  <p className="section-description">
                    Customize the appearance of your generated PDF reports
                  </p>

                  <div className="settings-form">
                {/* Logo Upload */}
                <div className="form-group">
                  <label className="form-label">Report Logo</label>
                  <p className="form-hint">Upload a custom logo to appear in the report header. Recommended: PNG or JPG, transparent background, max 2MB</p>
                  <div className="logo-upload-container">
                    <div
                      className={`logo-upload-area ${isDragging ? 'drag-over' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById('logo-file-input')?.click()}
                    >
                      <input
                        id="logo-file-input"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        style={{ display: 'none' }}
                      />
                      <div className="logo-preview-wrapper">
                        {logoPreview ? (
                          <div className="logo-preview">
                            <img src={logoPreview} alt="Logo preview" className="logo-preview-image" />
                          </div>
                        ) : (
                          <div className="logo-placeholder">
                            <div className="logo-placeholder-icon">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                <polyline points="21 15 16 10 5 21"></polyline>
                              </svg>
                            </div>
                            <div className="logo-placeholder-text">
                              <span>Drop your logo here or click to browse</span>
                              <span>PNG, JPG, or GIF • Max 2MB</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {logoPreview && (
                      <div className="logo-upload-actions">
                        <button
                          type="button"
                          className="remove-button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            
                            // If it's a Supabase Storage URL, try to delete the file
                            if (logoPreview.startsWith('http') && logoPreview.includes('supabase.co/storage')) {
                              try {
                                const { data: { session } } = await supabase.auth.getSession();
                                if (session?.user) {
                                  // Extract file path from URL
                                  const urlParts = logoPreview.split('/report-logos/');
                                  if (urlParts.length > 1) {
                                    const filePath = urlParts[1]; // path within the bucket
                                    await supabase.storage
                                      .from('report-logos')
                                      .remove([filePath]);
                                  }
                                }
                              } catch (error) {
                                console.error('Error deleting logo from storage:', error);
                                // Continue anyway - we'll still remove it from settings
                              }
                            }
                            
                            setLogoPreview(null);
                            setReportSettings(prev => ({
                              ...prev,
                              logoUrl: '/logo.png'
                            }));
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          Remove Logo
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Left Footer Text */}
                <div className="form-group">
                  <label className="form-label" htmlFor="leftFooter">
                    Left Footer Text
                  </label>
                  <p className="form-hint">Text to display on the left side of the footer</p>
                  <input
                    type="text"
                    id="leftFooter"
                    className="form-input"
                    value={reportSettings.leftFooterText}
                    onChange={(e) => handleInputChange('leftFooterText', e.target.value)}
                    placeholder="Generated by Parrot SEO"
                  />
                </div>

                {/* Right Footer Text */}
                <div className="form-group">
                  <label className="form-label" htmlFor="rightFooter">
                    Right Footer Text
                  </label>
                  <p className="form-hint">Text to display on the right side of the footer</p>
                  <input
                    type="text"
                    id="rightFooter"
                    className="form-input"
                    value={reportSettings.rightFooterText}
                    onChange={(e) => handleInputChange('rightFooterText', e.target.value)}
                    placeholder="parrotseo.com"
                  />
                </div>

                {/* Watermark Toggle */}
                <div className="form-group">
                  <label className="form-label">Watermark Image</label>
                  <p className="form-hint">Show or hide the watermark logo in the center of report pages</p>
                  <div className="toggle-container">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={reportSettings.watermarkEnabled}
                        onChange={handleToggleWatermark}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <span className="toggle-label">
                      {reportSettings.watermarkEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'license' && (
            <div className="settings-section">
              {!isProUser ? (
                <div className="account-pro-lock">
                  <div className="account-pro-lock-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </div>
                  <div className="account-pro-lock-content">
                    <h2 className="account-pro-lock-title">License Management is a Pro feature</h2>
                    <p className="account-pro-lock-text">
                      Upgrade your plan to unlock license key management and activate premium features.
                    </p>
                    <button
                      type="button"
                      className="account-pro-lock-button"
                      onClick={() => window.open('https://parrotseo.com/pricing', '_blank', 'noopener,noreferrer')}
                    >
                      Upgrade Plan
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2 className="section-title">License Management</h2>
                  <p className="section-description">
                    Enter your license key to activate premium features and unlock advanced capabilities
                  </p>

                  <div className="settings-form">
                    <div className="form-group">
                      <label className="form-label" htmlFor="licenseKey">
                        License Key
                      </label>
                      <p className="form-hint">
                        Enter your license key to activate your subscription. Your license key is typically provided via email after purchase.
                      </p>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <input
                          type="text"
                          id="licenseKey"
                          className="form-input"
                          value={licenseKey}
                          onChange={(e) => setLicenseKey(e.target.value)}
                          placeholder="Enter your license key (e.g., XXXXX-XXXXX-XXXXX-XXXXX)"
                          style={{ fontFamily: 'monospace', letterSpacing: '0.5px', flex: 1 }}
                        />
                        <button
                          type="button"
                          className="save-button"
                          onClick={handleLicenseSave}
                          disabled={licenseSaving}
                          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                          {licenseSaving ? 'Saving...' : licenseSaved ? '✓ Saved' : 'Save License'}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;

